import React, { useCallback, useMemo, useState } from 'react'

import { HiMiniCheck, HiMiniXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import IconButton, {
  type IconButtonProps,
} from '@/components/common/IconButtonV2'
import Popover from '@/components/common/PopoverV2'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { COLOR_CLASSNAMES, COLOR_LABELS, COLOR_PAIRS } from '@/constants/color'

import type { Color } from '@/types/Color'

interface ColorButtonProps {
  color: Color
  isActive: boolean
  onClick: () => void
  className?: string
  disabled?: boolean
}

interface ColorSelectorProps {
  color?: Color
  onChange: (color: Color) => void
  indicatorClassName?: string
  triggerProps?: Omit<IconButtonProps, 'type' | 'ref'>
  disabledColors?: Color[]
  disabled?: boolean
}

const ColorButton: React.FC<ColorButtonProps> = ({
  color,
  isActive,
  onClick,
  className,
  disabled = false,
}) => {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (disabled) return
      onClick()
    },
    [disabled, onClick],
  )

  const tooltipLabel = disabled
    ? `${COLOR_LABELS[color]} (in use)`
    : COLOR_LABELS[color]

  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild={true}>
        <button
          type="button"
          className={twMerge(
            'flex items-center justify-center w-[20px] h-[20px] rounded-[3px] cursor-pointer hover:opacity-60 transition-opacity duration-150',
            COLOR_CLASSNAMES[color],
            disabled
              ? 'cursor-not-allowed opacity-40 hover:opacity-40'
              : undefined,
            className,
          )}
          onClick={handleClick}
          aria-disabled={disabled ? 'true' : 'false'}
          tabIndex={disabled ? -1 : 0}
          disabled={disabled}
        >
          {isActive ? (
            <HiMiniCheck className="w-[14px] h-[14px] stroke-[1px]" />
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltipLabel}</TooltipContent>
    </Tooltip>
  )
}

export const ColorPicker: React.FC<
  ColorSelectorProps & { className?: string }
> = ({
  color: currentColor,
  onChange,
  className,
  disabledColors,
  disabled = false,
}) => {
  const disabledColorSet = useMemo(() => {
    return new Set(disabledColors ?? [])
  }, [disabledColors])

  const handleChange = (color: Color) => () => {
    onChange(color)
  }
  return (
    <div className={twMerge('flex flex-wrap gap-1 p-1', className)}>
      {COLOR_PAIRS.map(({ left, right }, pairIndex) => {
        return (
          <div key={pairIndex} className="flex flex-col gap-1">
            <ColorButton
              color={left.color}
              isActive={currentColor === left.color}
              onClick={handleChange(left.color as Color)}
              disabled={
                disabled ||
                (disabledColorSet.has(left.color) &&
                  currentColor !== left.color)
              }
            />
            {right && (
              <ColorButton
                color={right.color}
                isActive={currentColor === right.color}
                onClick={handleChange(right.color as Color)}
                disabled={
                  disabledColorSet.has(right.color) &&
                  currentColor !== right.color
                }
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

const handleOpenAutoFocus = (event: Event) => {
  event.preventDefault()
}

export const ColorSelector: React.FC<ColorSelectorProps> = ({
  color: currentColor,
  onChange,
  triggerProps,
  indicatorClassName,
  disabledColors,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const {
    className: triggerClassName,
    children: triggerChildren,
    ...triggerRest
  } = triggerProps || {}

  return (
    <Popover open={isOpen && !disabled} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <IconButton
          className={twMerge(
            'flex items-center justify-center w-6 h-6 rounded-[5px]',
            triggerClassName,
          )}
          type="button"
          disabled={disabled}
          {...triggerRest}
        >
          {triggerChildren || (
            <ColorIndicator
              color={currentColor || 'white'}
              className={indicatorClassName}
            />
          )}
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        className="p-1 w-[304px] space-y-[2px]"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <div className="flex items-center justify-between pl-1 text-sm font-medium text-primary-700 dark:text-primary-300">
          <span>Select a color</span>
          <IconButton
            onClick={handleClose}
            tooltip="Close"
            tooltipProps={{ delayDuration: 500 }}
          >
            <HiMiniXMark className="w-4 h-4" />
          </IconButton>
        </div>
        <ColorPicker
          color={currentColor}
          onChange={onChange}
          disabledColors={disabledColors}
        />
      </Popover.Content>
    </Popover>
  )
}
